import { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { notEmpty } from '@crehana/utils';

import type { SocialCompanyType } from './constants';
import GET_TRUSTPULSE_COMPANIES from './graphql/GetTrustPulseCompanies.v4.b2b.graphql';
import type {
  GetTrustPulseCompanies,
  GetTrustPulseCompaniesVariables,
} from './graphql/types/GetTrustPulseCompanies';

interface useTrustPulseNotificationProps {
  /**
   * Current testId given by VWO randomization
   */
  ABTestVariation?: string;
  /**
   * The id you set up to run the test i.e: '1' | '2' | '3' ...
   */
  testId?: string;
}

const useTrustPulseNotification = ({
  ABTestVariation,
  testId,
}: useTrustPulseNotificationProps) => {
  const { data, loading } = useQuery<
    GetTrustPulseCompanies,
    GetTrustPulseCompaniesVariables
  >(GET_TRUSTPULSE_COMPANIES, {
    context: { clientName: 'v4.b2b' },
  });
  const [openTrustPulse, setOpenTrustPulse] = useState(-1);
  const [trustPulseCompany, setTrustPulseCompany] =
    useState<SocialCompanyType | null>(null);

  const companies =
    data?.b2bOrganizationListTrust?.edges.filter(notEmpty).map(el => ({
      id: parseInt(el.node.originalId, 10),
      name: el.node.name,
      image: el.node.logo?.url || '',
    })) || [];

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    if (ABTestVariation === testId && !loading) {
      if (companies.length > 0) {
        let timeout: number;
        let innerTimeout: number;

        const secondsPerNotification = 5000;
        const secondsBetweenNotification = 3000;

        companies.forEach((company, idx) => {
          const isCrehana = company.name.includes('crehana');

          if (!isCrehana) {
            timeout = window.setTimeout(
              () => {
                setTrustPulseCompany(company);
                setOpenTrustPulse(company.id);

                innerTimeout = window.setTimeout(() => {
                  setOpenTrustPulse(-1);
                }, secondsPerNotification);
              },
              idx * secondsPerNotification + secondsBetweenNotification * idx,
            );
          }
        });

        return () => {
          clearTimeout(timeout);
          clearTimeout(innerTimeout);
        };
      }
    }
  }, [setOpenTrustPulse, ABTestVariation, loading]);

  return { openTrustPulse, trustPulseCompany };
};

export default useTrustPulseNotification;
